import React, { useEffect, useState, useRef, useMemo } from 'react';
import { longestCommonStringFromStart } from '../../utils/helpers';

interface ValueChangeComponentProps {
  value: number;
  className?: string;
  style?: React.CSSProperties;
}

const ValueChangeComponent: React.FC<ValueChangeComponentProps> = ({ value, className, style }) => {
  const previousValueRef = useRef(value);  // Track the previous value using a ref
  const [changeColor, setChangeColor] = useState<string>('');
  const [isFirstChange, setIsFirstChange] = useState<boolean>(true);  // Flag for initial state
  
  // Format values to 2 decimal places and then convert to strings
  const valueString = useMemo(() => value.toFixed(2), [value]);
  const previousValueString = useMemo(() => previousValueRef.current.toFixed(2), [value]);

  // If this is the first render, treat the whole value as non-changing
  const nonChanging = useMemo(() => {
    return isFirstChange ? valueString : longestCommonStringFromStart(valueString, previousValueString);
  }, [valueString, previousValueString, isFirstChange]);

  // Only calculate the changing part after the common part
  const changing = valueString.substring(nonChanging.length);
  
  useEffect(() => {
    // Compare the current value with previous value and set color
    if (!isFirstChange) {
      if (value > previousValueRef.current) {
        setChangeColor('green');
      } else if (value < previousValueRef.current) {
        setChangeColor('red');
      } else {
        setChangeColor(''); // Reset color if no change
      }
    }

    // Update the ref to the current value for the next comparison
    previousValueRef.current = value;

    // Set the first change flag to false after the first comparison
    if (isFirstChange) {
      setIsFirstChange(false);
    }
  }, [value, isFirstChange]);

  return (
    <span className={className} style={style}>
      <span>{nonChanging}</span>
      <span style={{ color: changeColor }}>
        {changing}
      </span>
    </span>
  );
};

export default ValueChangeComponent;
